// 이용약관 v7.0 제4조의7(환자 개인정보의 처리위탁) 재확인 모달 — 14항 (2026-10-06 코드 준비, 시행 2026-11-06 후 isTermsV70Effective 로만 뜬다)
// 띄우는 곳: MainLayout(로그인 후 세션당 1회 자동 + 잠금 화면 [확인하기] 요청 시). 판정: utils/delegationV70.js useDelegationV70.
// 대표: 요지 + [전문 보기](/terms 새 탭, 시행 후라 v7.0 이 현행으로 보임) + [확인] → users/{uid}.consents.delegation = {agreed, version:'v7.0', agreedAt, textHash}
//   textHash = termsV70.js V70_ART4_7_TEXT 의 sha256(careLegal normalize 규칙). 실행 시 재계산해 V70_ART4_7_TEXT_HASH 와 다르면 기록하지 않는다(fail-closed — 원문 수정 후 해시 미갱신 방지).
//   [나중에] = 이번 세션에는 다시 띄우지 않음. 잠금(환자 관리 기능)은 확인 전까지 유지, 그 밖의 기능은 정상(14항).
// 직원: 대표에게 안내만([닫기]). 직원 본인 문서에는 아무것도 쓰지 않는다.
// rules: users 본인 update 는 consents 키를 막지 않는다(firestore.rules users update 1번 절 blacklist 밖) — rules 변경 없음.
// 서버 subscription-billing.js TERMS_VERSION(requiresReconsent 판정)은 dev-server 가 같은 시행 시각에 맞춰 바꾼다.
import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { V70_ART4_7, V70_ART4_7_TEXT, V70_ART4_7_TEXT_HASH, V70_DELEGATION_SUMMARY, V70_DELEGATION_VERSION, TERMS_V70_EFFECTIVE_DATE } from '../../config/termsV70';
import { sha256Hex } from '../../config/careLegal';

function DelegationV70ReconfirmModal({ user, isStaff, onClose, onConfirmed }) {
    // 훅은 전부 최상단
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const confirm = async () => {
        if (busy || isStaff || !user?.uid) return;
        setBusy(true); setErr('');
        try {
            const h = await sha256Hex(V70_ART4_7_TEXT);
            if (!h || h !== V70_ART4_7_TEXT_HASH) {
                console.error('[약관 v7.0 재확인] 제4조의7 원문 해시 불일치 — 기록하지 않음', h ? '(계산값 다름)' : '(해시 계산 불가)');
                setErr('확인을 기록할 수 없습니다(문안 대조 실패). 고객센터에 알려 주세요.');
                return;
            }
            const agreedAt = new Date().toISOString();
            await updateDoc(doc(db, 'users', user.uid), {
                'consents.delegation': { agreed: true, version: V70_DELEGATION_VERSION, agreedAt, textHash: h },
                updatedAt: agreedAt,
            });
            onConfirmed?.();
        } catch (e) {
            console.error('[약관 v7.0 재확인] 기록 실패:', e?.code || e?.message || e);
            setErr('확인을 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.');
        } finally { setBusy(false); }
    };

    return (
        <div style={M.backdrop} role="dialog" aria-modal="true" aria-label="이용약관 제4조의7 재확인">
            <div style={M.box}>
                <div style={M.title}>{'이용약관 제4조의7 재확인'}</div>
                <div style={M.sub}>{`이용약관 v7.0 이 ${TERMS_V70_EFFECTIVE_DATE}부터 시행되었습니다. ${V70_ART4_7.title}이 전면 개정되어, 환자 관리 기능을 계속 쓰려면 ${isStaff ? '치과 대표 계정의' : ''} 확인이 한 번 더 필요합니다.`}</div>
                <div style={M.summaryHead}>{'바뀐 내용 요지'}</div>
                <ul style={M.list}>
                    {V70_DELEGATION_SUMMARY.map((t) => <li key={t.slice(0, 10)} style={M.li}>{t}</li>)}
                </ul>
                <div style={M.note}>
                    {'요지는 안내용이며 동의 대상은 약관 전문의 제4조의7 입니다. '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={M.link}>{'전문 보기'}</a>
                </div>
                {isStaff ? (
                    <>
                        <div style={M.staffBox}>{'직원 계정은 확인할 수 없습니다. 치과 대표 계정으로 로그인해 확인해 주세요. 대표가 확인하기 전까지 환자 관리 기능(환자 명부·접수·재내원 안내·진료 후 질문·관련 설정)은 열리지 않으며, 그 밖의 기능은 그대로 쓸 수 있습니다.'}</div>
                        <div style={M.btnRow}>
                            <button type="button" onClick={onClose} style={M.btnS}>{'닫기'}</button>
                        </div>
                    </>
                ) : (
                    <>
                        <div style={M.hint}>{'[확인]을 누르면 확인 일시와 문안 식별값이 우리 치과 계정에 기록됩니다. [나중에]를 누르면 환자 관리 기능만 잠긴 채 다른 기능은 그대로 쓸 수 있고, 환자 관리 화면에서 언제든 다시 확인할 수 있습니다.'}</div>
                        {err && <div style={M.err}>{err}</div>}
                        <div style={M.btnRow}>
                            <button type="button" disabled={busy} onClick={onClose} style={{ ...M.btnS, opacity: busy ? 0.5 : 1 }}>{'나중에'}</button>
                            <button type="button" disabled={busy} onClick={confirm} style={{ ...M.btnP, opacity: busy ? 0.5 : 1 }}>{busy ? '기록 중…' : '확인'}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

const M = {
    backdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    box: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: '22px 24px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' },
    title: { fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 6 },
    sub: { fontSize: 13, color: '#475569', lineHeight: 1.65 },
    summaryHead: { fontSize: 13, fontWeight: 700, color: '#0f172a', marginTop: 14, marginBottom: 6 },
    list: { margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 },
    li: { fontSize: 12.5, color: '#1e293b', lineHeight: 1.6, wordBreak: 'keep-all' },
    note: { marginTop: 10, fontSize: 12, color: '#64748b', lineHeight: 1.6 },
    link: { color: '#4F52C7', fontWeight: 700, textDecoration: 'underline' },
    hint: { marginTop: 12, fontSize: 12, color: '#64748b', lineHeight: 1.6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 11px' },
    staffBox: { marginTop: 12, fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '9px 12px', lineHeight: 1.6 },
    err: { marginTop: 10, fontSize: 12.5, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 8, padding: '8px 11px' },
    btnRow: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
    btnS: { padding: '9px 16px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' },
    btnP: { padding: '9px 18px', background: '#0f6b5c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
};

export default DelegationV70ReconfirmModal;
